import React, {createContext, useContext, useEffect, useMemo, useState} from "react";
import {ConfigProvider, theme as antdTheme} from "antd";
import faIR from "antd/locale/fa_IR";
import {fetchBranding} from "./branding-api";
import {buildTheme} from "./design-tokens";

/**
 * Console theming: light/dark mode plus the admin-configured brand colours and logos.
 *
 * <p>Both concerns live together because they feed the same AntD ConfigProvider — the mode picks
 * the algorithm, the branding supplies the token colours. Mode is persisted per browser; branding
 * comes from the backend's public endpoint so the login page (which has no token yet) can render
 * it too.
 */
const ThemeContext = createContext({
    mode: "light",
    toggleMode: () => {},
    branding: null,
});

export const useAppTheme = () => useContext(ThemeContext);

const STORAGE_KEY = "npe-theme-mode";

const readInitialMode = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
    // Fall back to the OS preference the first time.
    return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
};

export const AppThemeProvider = ({children}) => {
    const [mode, setMode] = useState(readInitialMode);
    const [branding, setBranding] = useState(null);

    // Derived first: the effect below mirrors these tokens into CSS custom properties.
    const design = useMemo(
        () => buildTheme(mode, branding?.primaryColor),
        [mode, branding]
    );

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, mode);
        document.documentElement.setAttribute("data-theme", mode);

        // Mirror the design tokens to CSS custom properties for the places that can't read AntD
        // tokens: inline <style> blocks (table zebra striping) and inline styles on plain elements.
        // Defining them in one place is what keeps every screen consistent.
        const root = document.documentElement.style;
        Object.entries(design.cssVars).forEach(([k, v]) => root.setProperty(k, v));

        document.body.style.background = design.cssVars["--npe-canvas"];
        document.body.style.color = design.cssVars["--npe-text"];
        // Tells the browser to render form controls/scrollbars in the matching scheme.
        document.body.style.colorScheme = mode;
    }, [mode, design]);

    useEffect(() => {
        let cancelled = false;
        fetchBranding()
            .then((b) => {
                if (!cancelled) setBranding(b);
            })
            .catch(() => {
                // Branding is cosmetic — a failure must never block the app from rendering.
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const toggleMode = () => setMode((m) => (m === "dark" ? "light" : "dark"));

    const themeConfig = useMemo(
        () => ({
            algorithm: mode === "dark" ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
            token: design.token,
            components: design.components,
        }),
        [mode, design]
    );

    const value = useMemo(() => ({mode, toggleMode, branding}), [mode, branding]);

    return (
        <ThemeContext.Provider value={value}>
            <ConfigProvider locale={faIR} direction="rtl" theme={themeConfig}>
                {children}
            </ConfigProvider>
        </ThemeContext.Provider>
    );
};

export default ThemeContext;
